import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { Button, Chip, Stack, Theme, Typography } from '@mui/material';
import * as React from 'react';
import { useHistory } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import { settingsRoutes } from '../../../../routes';
import { ChannelItemSms, ChannelItemSmsMedia } from '../../../../sic-lib/types/ChannelItem';
import { UserRoles, userRoles } from '../../../../sic-lib/types/UserRoles';
import { isUserInRole } from '../../../../sic-lib/utils/userHelpers';

interface Props {
    channelItem: ChannelItemSms;
    roles: UserRoles[];
}

interface GroupedMedia {
    images: ChannelItemSmsMedia[];
    videos: ChannelItemSmsMedia[];
    audios: ChannelItemSmsMedia[];
    others: ChannelItemSmsMedia[];
}

export default React.memo(function ConversationListItemMessage({ channelItem, roles }: Props) {
    const { classes } = useStyles();
    const history = useHistory();

    const handleAiSrClick = React.useCallback(() => {
        if (channelItem.srContactPlanItemId && isUserInRole(roles, userRoles.techSupport)) {
            history.push(settingsRoutes.setAiContactPlanItemEvent(channelItem.srContactPlanItemId));
        }
    }, [channelItem.srContactPlanItemId, history, roles]);

    const groupedMedia = React.useMemo(() => {
        const initialGroupedMedia: GroupedMedia = {
            images: [],
            videos: [],
            audios: [],
            others: [],
        };
        return (
            channelItem.media?.reduce((acc, media) => {
                if (
                    ['image/jpeg', 'image/webp', 'image/png', 'image/gif'].includes(
                        media.contentType,
                    )
                ) {
                    acc.images.push(media);
                } else if (['video/mp4', 'video/quicktime'].includes(media.contentType)) {
                    acc.videos.push(media);
                } else if (['audio/mpeg', 'audio/mp4'].includes(media.contentType)) {
                    acc.audios.push(media);
                } else {
                    acc.others.push(media);
                }
                return acc;
            }, initialGroupedMedia) ?? initialGroupedMedia
        );
    }, [channelItem]);

    return (
        <>
            {channelItem.message && (
                <Typography variant="body1" display="block" whiteSpace="pre-wrap" p={2}>
                    {channelItem.message}
                </Typography>
            )}
            {!!channelItem.srContactPlanItemId && (
                <Stack justifyContent="center">
                    <Chip
                        label="Sent by AI Smart Reply"
                        avatar={<PsychologyIcon style={{ color: '#fff' }} />}
                        sx={{ mb: 2, mr: 2, ml: 2, color: '#fff' }}
                        onClick={handleAiSrClick}
                    />
                </Stack>
            )}
            {channelItem.media && channelItem.media.length > 0 && (
                <Stack spacing={2} p={2}>
                    {groupedMedia.images.map((media, index) => (
                        <a
                            href={media.mediaUrl}
                            target="_blank"
                            rel="noreferrer"
                            className={classes.a}
                            key={index}
                        >
                            <img
                                src={media.mediaUrl}
                                alt="Media content"
                                className={classes.media}
                            />
                        </a>
                    ))}
                    {groupedMedia.videos.map((media, index) => (
                        <video
                            key={index}
                            src={media.mediaUrl}
                            controls
                            className={classes.media}
                        />
                    ))}
                    {groupedMedia.audios.map((media, index) => (
                        <audio key={index} src={media.mediaUrl} controls className={classes.flex} />
                    ))}
                    {groupedMedia.others.map((media, index) => (
                        <Button
                            key={index}
                            href={media.mediaUrl}
                            target="_blank"
                            rel="noreferrer"
                            variant="text"
                            startIcon={<OpenInNewIcon />}
                            className={classes.button}
                        >
                            View Unsupported Media
                        </Button>
                    ))}
                </Stack>
            )}
            {/* <DebugObject object={channelItem} /> */}
        </>
    );
});

const useStyles = makeStyles()((theme: Theme) => ({
    media: {
        maxWidth: '300px',
        maxHeight: '300px',
        display: 'flex',
    },
    a: {
        display: 'block',
    },
    flex: {
        display: 'flex',
    },
    button: {
        color: '#fff',
    },
}));
